import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Product, ProductType } from "@/types/api";

export interface CartItem {
  productId: string;
  sku: string;
  slug: string;
  name: string;
  productType: ProductType;
  certificateKind?: "ECPF" | "ECNPJ" | "EMEI" | null;
  certificateType?: "A1" | "A3" | null;
  priceCents: number;
  quantity: number;
  imageUrl?: string | null;
}

interface AddProductOptions {
  /** When true, skips opening the CartAddedModal. Used by buy-now flows that
   * immediately redirect to /checkout — otherwise the modal "Pagar agora"
   * competes with the checkout page the user is being sent to. */
  silent?: boolean;
}

interface CartStore {
  items: CartItem[];
  showCartModal: boolean;
  lastAddedProduct: CartItem | null;
  addProduct: (product: Product, quantity?: number, opts?: AddProductOptions) => void;
  closeCartModal: () => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clear: () => void;
  totalItems: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      showCartModal: false,
      lastAddedProduct: null,
      addProduct: (product, quantity = 1, opts = {}) => {
        const existing = get().items.find((item) => item.productId === product.id);
        const newItem: CartItem = {
          productId: product.id,
          sku: product.sku,
          slug: product.slug,
          name: product.name,
          productType: product.productType,
          certificateKind: product.certificateKind ?? null,
          certificateType: product.certificateType ?? null,
          priceCents: product.priceCents,
          quantity,
          imageUrl: product.imageUrl
        };
        const showCartModal = !opts.silent;

        if (existing) {
          set({
            items: get().items.map((item) =>
              item.productId === product.id
                ? { ...item, quantity: item.quantity + quantity }
                : item
            ),
            showCartModal,
            lastAddedProduct: newItem
          });
          return;
        }

        set({
          items: [...get().items, newItem],
          showCartModal,
          lastAddedProduct: newItem
        });
      },
      closeCartModal: () => set({ showCartModal: false }),
      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          set({ items: get().items.filter((item) => item.productId !== productId) });
          return;
        }

        set({
          items: get().items.map((item) =>
            item.productId === productId ? { ...item, quantity } : item
          )
        });
      },
      removeItem: (productId) => {
        set({ items: get().items.filter((item) => item.productId !== productId) });
      },
      clear: () => set({ items: [] }),
      totalItems: () => get().items.reduce((sum, item) => sum + item.quantity, 0)
    }),
    {
      name: "certi-cart-storage",
      storage: createJSONStorage(() => localStorage)
    }
  )
);
